import { AnalyticsEvents } from '@mondaygpt/analytics-events'
import { useCallback } from 'react'
import { weeklyEngagementService, type EngagementThreshold } from 'services/weekly-engagement'
import { useAnalytics } from './use-analytics'

/**
 * Map threshold strings to their corresponding analytics events
 */
const THRESHOLD_TO_EVENT: Record<EngagementThreshold, string> = {
  '1o1m': AnalyticsEvents.Engagement.ENGAGEMENT_1O1M,
  '1o2m': AnalyticsEvents.Engagement.ENGAGEMENT_1O2M,
  '1o3m': AnalyticsEvents.Engagement.ENGAGEMENT_1O3M,
  '2o1m': AnalyticsEvents.Engagement.ENGAGEMENT_2O1M,
  '2o2m': AnalyticsEvents.Engagement.ENGAGEMENT_2O2M,
  '2o3m': AnalyticsEvents.Engagement.ENGAGEMENT_2O3M,
  '3o1m': AnalyticsEvents.Engagement.ENGAGEMENT_3O1M,
  '3o2m': AnalyticsEvents.Engagement.ENGAGEMENT_3O2M,
  '3o3m': AnalyticsEvents.Engagement.ENGAGEMENT_3O3M,
}

/**
 * Hook for tracking weekly user engagement (opens and messages)
 * Uses the existing useAnalytics hook to report threshold events
 *
 * Tracks:
 * - Opens: Unique days with at least one app open in a calendar week (Mon-Sun)
 * - Messages: Total messages sent in a calendar week
 *
 * Reports events when thresholds are reached:
 * ENGAGEMENT_1O1M, ENGAGEMENT_1O2M, ENGAGEMENT_1O3M,
 * ENGAGEMENT_2O1M, ENGAGEMENT_2O2M, ENGAGEMENT_2O3M,
 * ENGAGEMENT_3O1M, ENGAGEMENT_3O2M, ENGAGEMENT_3O3M
 */
export const useWeeklyEngagement = () => {
  const { track } = useAnalytics()

  const recordOpen = useCallback(() => {
    const { newThresholds } = weeklyEngagementService.recordOpen()

    // Report each newly crossed threshold as its own event
    newThresholds.forEach(threshold => {
      const eventName = THRESHOLD_TO_EVENT[threshold]
      if (eventName) {
        track(eventName)
      }
    })
  }, [track])

  const recordMessage = useCallback(() => {
    const { newThresholds } = weeklyEngagementService.recordMessage()

    // Report each newly crossed threshold as its own event
    newThresholds.forEach(threshold => {
      const eventName = THRESHOLD_TO_EVENT[threshold]
      if (eventName) {
        track(eventName)
      }
    })
  }, [track])

  return { recordOpen, recordMessage }
}
